import * as React from 'react';
import { createContext, useContext, useState, useEffect } from 'react';
import { DEFAULT_HIDDEN_MENU_IDS } from '@/lib/navigation';
import { supabase } from '@/lib/supabase';
import { useAuth } from './AuthContext';
import { injectThemeColors, removeThemeColors } from '@/lib/themeInjector';
import { safeLocalStorage } from '@/lib/storage-utils';
import { sessionCache } from '@/lib/sessionCache';

interface WorkspaceAppearance {
  logo?: string;
  favicon?: string;
  theme?: {
    backgroundColor?: string;
    accentColor?: string;
    sidebarColor?: string;
    hoverColor?: string;
    textColor?: string;
    linkColor?: string;
  };
  loginPage?: {
    backgroundImage?: string;
    backgroundColor?: string;
    logo?: string;
    title?: string;
    subtitle?: string;
    buttonColor?: string;
    textColor?: string;
  };
  menu?: {
    hiddenIds?: string[];
    roleHiddenIds?: Record<string, string[]>;
  };
}

export interface Workspace {
  id: string;
  name: string;
  color?: string;
  logo?: string;
  thumbnail?: string;
  favicon?: string;
  banner_image?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  appearance?: WorkspaceAppearance;
  custom_domain?: string;
  custom_domain_status?: string;
  custom_domain_verification_record?: any;
  custom_hostname_id?: string;
  custom_email_domain?: string;
  language?: string;
  currency?: string;
  isCurrent?: boolean;
  userRole?: 'owner' | 'admin' | 'manager' | 'member';
}

interface PublicWorkspace {
  id: string;
  name: string;
  logo: string;
  appearance: WorkspaceAppearance;
}

interface WorkspaceContextType {
  currentWorkspace: Workspace | null;
  workspaces: Workspace[];
  userRole: 'owner' | 'admin' | 'manager' | 'member' | 'client' | null;
  isClient: boolean;
  loading: boolean;
  publicWorkspace: PublicWorkspace | null;
  isCustomDomain: boolean;
  setCurrentWorkspace: (workspace: Workspace) => void;
  refreshWorkspaces: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, signOut } = useAuth();
  const [currentWorkspace, setCurrentWorkspaceState] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [userRole, setUserRole] = useState<'owner' | 'admin' | 'manager' | 'member' | 'client' | null>(null);
  const [loading, setLoading] = useState(true);
  const [prevUserId, setPrevUserId] = useState<string | null>(null);
  const [publicWorkspace, setPublicWorkspace] = useState<PublicWorkspace | null>(null);
  const [isCustomDomain, setIsCustomDomain] = useState(false);

  // Sync loading state and hydrate cache when user changes
  const currentId = user?.id || null;
  if (currentId !== prevUserId) {
    setPrevUserId(currentId);
    if (user) {
      // Set loading true initially until we've confirmed cache existence
      if (!loading) setLoading(true);
      
      // Attempt immediate hydration from cache to prevent flickering
      const cachedWorkspaces = sessionCache.get<Workspace[]>(`workspaces_${user.id}`);
      const cachedCurrent = sessionCache.get<Workspace>(`currentWorkspace_${user.id}`);
      
      if (cachedWorkspaces && JSON.stringify(cachedWorkspaces) !== JSON.stringify(workspaces)) {
        setWorkspaces(cachedWorkspaces);
      }
      
      if (cachedCurrent && cachedCurrent.id !== currentWorkspace?.id) {
        setCurrentWorkspaceState(cachedCurrent);
        const cachedRole = sessionCache.get<'owner' | 'admin' | 'manager' | 'member'>(`userRole_${user.id}_${cachedCurrent.id}`);
        if (cachedRole !== userRole) setUserRole(cachedRole);
      }
    }
  }

  // Detect custom domain on mount
  useEffect(() => {
    const detectCustomDomain = async () => {
      const hostname = window.location.hostname;
      // Define your base domains here
      const isBaseDomain = 
        hostname === 'agencyplus.io' || 
        hostname.endsWith('.agencyplus.io') || 
        hostname === 'app.agencyplus.io' ||
        hostname.endsWith('.agencyplus.io') ||
        hostname === 'localhost' || 
        hostname === '127.0.0.1' ||
        hostname.includes('stackblitz.io') ||
        hostname.includes('bolt.new');
      
      if (!isBaseDomain) {
        setIsCustomDomain(true);
        try {
          // Fetch public workspace info using RPC
          const { data, error } = await supabase.rpc('get_workspace_by_domain', { domain_name: hostname });
          
          if (error) {
            console.error('Error fetching workspace by domain:', error);
            return;
          }

          if (data && data.length > 0) {
            setPublicWorkspace(data[0]);
          }
        } catch (err) {
          console.error('Detection error:', err);
        }
      }
    };
    detectCustomDomain();
  }, []);

  // Load workspaces
  const loadWorkspaces = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Get all workspaces where user is a member
      const { data: memberData, error: memberError } = await supabase
        .from('workspace_members')
        .select('workspace_id, role')
        .eq('user_id', user.id)
        .eq('status', 'active');

      if (memberError) throw memberError;

      if (!memberData || memberData.length === 0) {
        setLoading(false);
        setWorkspaces([]);
        setCurrentWorkspaceState(null);
        return;
      }

      // Load workspace details
      const workspaceIds = memberData.map(m => m.workspace_id);
      const { data: workspacesData, error: workspacesError } = await supabase
        .from('workspaces')
        .select('*')
        .in('id', workspaceIds)
        .order('created_at', { ascending: false });

      if (workspacesError) throw workspacesError;

      const processedWorkspaces = (workspacesData || []).map(w => {
        if (!w.appearance?.menu?.hiddenIds) {
          return {
            ...w,
            appearance: {
              ...w.appearance,
              menu: {
                ...w.appearance?.menu,
                hiddenIds: DEFAULT_HIDDEN_MENU_IDS
              }
            }
          };
        }
        return w;
      });

      setWorkspaces(processedWorkspaces);
      sessionCache.set(`workspaces_${user.id}`, processedWorkspaces);

      // If we're on a custom domain, prioritize that workspace
      if (publicWorkspace) {
        const workspaceOnDomain = processedWorkspaces.find(w => w.id === publicWorkspace.id);
        if (workspaceOnDomain) {
          const memberInfo = memberData.find(m => m.workspace_id === workspaceOnDomain.id);
          setCurrentWorkspaceState(workspaceOnDomain);
          setUserRole(memberInfo?.role || null);
          setLoading(false);
          return;
        }
      }

      // Set current workspace from localStorage or use first workspace
      const savedWorkspaceId = safeLocalStorage.getItem('currentWorkspaceId');
      let workspace = processedWorkspaces.find(w => w.id === savedWorkspaceId) || processedWorkspaces[0] || null;

      if (workspace) {
        const memberInfo = memberData.find(m => m.workspace_id === workspace.id);
        const role = memberInfo?.role || null;
        setCurrentWorkspaceState(workspace);
        setUserRole(role);
        safeLocalStorage.setItem('currentWorkspaceId', workspace.id);
        
        // Cache current workspace and role
        sessionCache.set(`currentWorkspace_${user.id}`, workspace);
        sessionCache.set(`userRole_${user.id}_${workspace.id}`, role);
      }

      setLoading(false);
    } catch (error: any) {
      console.error('Error loading workspaces:', error);
      // Handle expired JWT by signing out
      if (error?.code === 'PGRST303' || error?.message?.includes('JWT expired')) {
        console.warn('JWT expired dectected in loadWorkspaces, signing out...');
        signOut();
      }
      setLoading(false);
    }
  };

  // Apply theme colors when workspace or public workspace changes
  useEffect(() => {
    const activeWorkspace = currentWorkspace || publicWorkspace;
    if (activeWorkspace) {
      // Default Agency+ Colors
      const defaultTheme = {
        backgroundColor: '#FFFFFF',
        accentColor: '#3B82F6',
        sidebarColor: '#EFF6FF',
        hoverColor: '#BFDBFE',
        textColor: '#111827',
        linkColor: '#3B82F6',
      };

      // Use appearance settings from the workspace object
      const appearance = activeWorkspace.appearance;
      if (appearance?.theme) {
        injectThemeColors({
          ...defaultTheme,
          ...appearance.theme,
        });
      } else {
        injectThemeColors(defaultTheme);
      }
    } else {
      removeThemeColors();
    }
  }, [currentWorkspace?.id, publicWorkspace?.id]);



  // Skip auto-detect when not user but loading
  useEffect(() => {
    if (user) {
      // Hydrate from cache immediately for instant UI
      const cachedWorkspaces = sessionCache.get<Workspace[]>(`workspaces_${user.id}`);
      const cachedCurrent = sessionCache.get<Workspace>(`currentWorkspace_${user.id}`);
      
      if (cachedWorkspaces) setWorkspaces(cachedWorkspaces);
      if (cachedCurrent) {
        setCurrentWorkspaceState(cachedCurrent);
        const cachedRole = sessionCache.get<'owner' | 'admin' | 'manager' | 'member'>(`userRole_${user.id}_${cachedCurrent.id}`);
        if (cachedRole) setUserRole(cachedRole);
      }

      loadWorkspaces();
    } else {
      setWorkspaces([]);
      setCurrentWorkspaceState(null);
      setUserRole(null);
      setLoading(false);
      removeThemeColors();
    }
  // Depend on user?.id (not the user object itself) so a token refresh that
  // produces a new session object doesn't trigger a full workspace reload.
  }, [user?.id, publicWorkspace?.id]); // Re-load when publicWorkspace is detected

  // Set current workspace and save to localStorage
  const setCurrentWorkspace = async (workspace: Workspace) => {
    if (!user) return;

    // Apply default hidden IDs if not present
    if (!workspace.appearance?.menu?.hiddenIds) {
      workspace = {
        ...workspace,
        appearance: {
          ...workspace.appearance,
          menu: {
            ...workspace.appearance?.menu,
            hiddenIds: DEFAULT_HIDDEN_MENU_IDS
          }
        }
      };
    }

    // Get user role in this workspace
    const { data: memberData } = await supabase
      .from('workspace_members')
      .select('role')
      .eq('workspace_id', workspace.id)
      .eq('user_id', user.id)
      .eq('status', 'active')
      .limit(1)
      .maybeSingle();

    setCurrentWorkspaceState(workspace);
    setUserRole(memberData?.role || null);
    safeLocalStorage.setItem('currentWorkspaceId', workspace.id);

    // Domain Redirection Logic
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    const isDevelopment = 
      hostname === 'localhost' || 
      hostname === '127.0.0.1' || 
      hostname.includes('stackblitz.io') ||
      hostname.includes('bolt.new');

    const MAIN_APP_DOMAIN = 'app.agencyplus.io';
    const currentPath = window.location.pathname + window.location.search;

    /**
     * Build a cross-domain redirect URL that carries the current Supabase session
     * tokens in the URL hash. Supabase's detectSessionInUrl will pick these up
     * on the destination domain and restore the session — no re-login required,
     * and no risk of loading a different user's cached session.
     */
    const buildRedirectUrl = async (targetDomain: string): Promise<string> => {
      const { data: { session } } = await supabase.auth.getSession();
      const base = `${protocol}//${targetDomain}${currentPath}`;
      if (!session?.access_token || !session?.refresh_token) return base;
      return `${base}#access_token=${encodeURIComponent(session.access_token)}&refresh_token=${encodeURIComponent(session.refresh_token)}&token_type=bearer`;
    };

    // 1. If switching to a workspace with an active custom domain
    if (workspace.custom_domain && workspace.custom_domain_status === 'active') {
      if (hostname !== workspace.custom_domain && !isDevelopment) {
        const url = await buildRedirectUrl(workspace.custom_domain);
        window.location.href = url;
        return;
      }
    } 
    // 2. If switching to a workspace WITHOUT an active custom domain, 
    // AND we are currently on a custom domain (not the main app or dev)
    else if (hostname !== MAIN_APP_DOMAIN && !isDevelopment) {
      const url = await buildRedirectUrl(MAIN_APP_DOMAIN);
      window.location.href = url;
      return;
    }
  };

  const refreshWorkspaces = async () => {
    await loadWorkspaces();
  };

  const isClient = userRole === 'client';

  return (
    <WorkspaceContext.Provider
      value={{
        currentWorkspace,
        workspaces,
        userRole,
        isClient,
        loading,
        publicWorkspace,
        isCustomDomain,
        setCurrentWorkspace,
        refreshWorkspaces,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};
